import { useMemo } from 'react';
import { useCurrency } from '../context/CurrencyContext';

interface UsePriceProps {
  amount: number | undefined;
  currencyCode: string;
  locale?: string;
  options?: Intl.NumberFormatOptions;
}

interface UsePriceReturn {
  price: string;
  priceWithSymbol: string;
  currencySymbol: string;
  formattedValue: string;
  formattedValueWithSymbol: string;
}

/**
 * Hook for formatting prices with currency symbols
 * Leverages the CurrencyContext for consistent currency formatting
 */
const usePrice = ({
  amount,
  currencyCode,
  locale = 'en-US',
  options = {}
}: UsePriceProps): UsePriceReturn => {
  // Get currency context for formatting
  const currencyContext = useCurrency();
  
  return useMemo(() => {
    // Handle undefined or NaN amount
    if (typeof amount !== 'number' || Number.isNaN(amount)) {
      return {
        price: '0.00',
        priceWithSymbol: '$0.00',
        currencySymbol: '$',
        formattedValue: '0.00',
        formattedValueWithSymbol: '$0.00'
      };
    }

    // Map of currency symbols for quick access
    const currencySymbols: Record<string, string> = {
      USD: '$',
      ILS: '₪',
      EUR: '€',
      // Add more currencies as needed
    };

    // Get currency symbol - first try from our map, then from formatted string
    let currencySymbol = currencySymbols[currencyCode] || '';
    
    // If we don't have a direct mapping, use Intl API to extract it
    if (!currencySymbol) {
      const formatter = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: 2,
        ...options
      });
      const formattedWithSymbol = formatter.format(0);
      const plainFormatter = new Intl.NumberFormat(locale, {
        style: 'decimal',
        minimumFractionDigits: 2,
        ...options
      });
      const formattedWithoutSymbol = plainFormatter.format(0);
      currencySymbol = formattedWithSymbol
        .replace(formattedWithoutSymbol, '')
        .trim();
    }

    // Try to use the context's formatPrice if the currency matches
    if (currencyCode === currencyContext.currency) {
      const formattedWithSymbol = currencyContext.formatPrice(amount, true);
      const formattedWithoutSymbol = currencyContext.formatPrice(amount, false);
      
      return {
        price: formattedWithoutSymbol,
        priceWithSymbol: formattedWithSymbol,
        currencySymbol: formattedWithSymbol.replace(formattedWithoutSymbol, '').trim(),
        formattedValue: formattedWithoutSymbol,
        formattedValueWithSymbol: formattedWithSymbol
      };
    }
    
    // Fall back to standard formatting
    const formatter = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 2,
      ...options
    });
    
    const plainFormatter = new Intl.NumberFormat(locale, {
      style: 'decimal',
      minimumFractionDigits: 2,
      ...options
    });
    
    const formattedWithSymbol = formatter.format(amount);
    const formattedWithoutSymbol = plainFormatter.format(amount);
    
    return {
      price: formattedWithoutSymbol,
      priceWithSymbol: formattedWithSymbol,
      currencySymbol,
      formattedValue: formattedWithoutSymbol,
      formattedValueWithSymbol: formattedWithSymbol
    };
  }, [amount, currencyCode, locale, options, currencyContext]);
};

export default usePrice;